import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Trophy, Dumbbell, ChevronRight, Shield } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import ApplicationWizard from './ApplicationWizard';
import FitnessWizard from './FitnessWizard';
import { captureAttribution } from '../utils/attribution';

const UnifiedWizard = () => {
  const [searchParams] = useSearchParams();
  const [selectedTrack, setSelectedTrack] = useState(null);

  // Capture UTM lead-source attribution on landing
  useEffect(() => {
    captureAttribution();
  }, []);

  // Check if track was pre-selected via query param
  useEffect(() => {
    const track = searchParams.get('track');
    if (track === 'sports') setSelectedTrack('sports');
    else if (track === 'fitness') setSelectedTrack('fitness');
  }, [searchParams]);

  // Once a track is selected, render the appropriate wizard
  if (selectedTrack === 'sports') {
    return <ApplicationWizard directAccess={true} />;
  }
  if (selectedTrack === 'fitness') {
    return <FitnessWizard directAccess={true} />;
  }

  // Track picker
  return (
    <div className="min-h-screen bg-[#F7F8FA]" style={{ fontFamily: "'Inter', sans-serif" }}>
      <Helmet>
        <title>Get a Quote | Players Health Insurance</title>
        <meta name="description" content="Get an insurance quote for your sports organization or fitness studio. Choose your track and complete a quick application." />
      </Helmet>

      {/* Header */}
      <header className="relative bg-[#232323] sticky top-0 z-50">
        <div className="h-[3px] bg-gradient-to-r from-[#E040FB] via-[#FF6B35] to-[#FFD93D]"></div>
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src="/logo-light.png" alt="Players Health" className="h-8" />
          <span className="text-sm font-semibold text-[#7B6FE8]" style={{ fontFamily: "'Inter', sans-serif" }}>
            Insurance Application
          </span>
        </div>
        <div className="h-[2px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4]"></div>
      </header>

      {/* Track Selection */}
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-[#EEF0FF] rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Shield className="h-8 w-8 text-[#7B6FE8]" />
          </div>
          <h1
            className="text-3xl sm:text-4xl font-black text-[#1A1A1A] uppercase tracking-wide mb-3"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            data-testid="track-picker-heading"
          >
            What type of organization are you?
          </h1>
          <p className="text-[#6B7280] max-w-lg mx-auto">
            Select your organization type and we'll tailor the application to match your coverage needs.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Sports Track */}
          <button
            onClick={() => setSelectedTrack('sports')}
            data-testid="track-select-sports"
            className="group bg-white border-2 border-[#E5E7EB] hover:border-[#6B5CE7] rounded-2xl p-8 text-left transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-[#6B5CE7]/10 relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="w-14 h-14 rounded-[14px] bg-gradient-to-br from-[#6B5CE7]/10 to-[#4ECDC4]/10 flex items-center justify-center mb-5">
              <Trophy className="h-7 w-7 text-[#7B6FE8]" />
            </div>
            <h3
              className="font-extrabold text-[#1A1A1A] text-xl uppercase mb-2 tracking-wide"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Youth & Amateur Sports
            </h3>
            <p className="text-[#6B7280] text-sm leading-relaxed mb-6">
              Leagues, clubs, camps, tournaments, and recreational sports organizations.
            </p>
            <span className="inline-flex items-center gap-1.5 text-sm font-bold text-[#7B6FE8] uppercase tracking-wider" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Start Application <ChevronRight className="h-4 w-4" />
            </span>
          </button>

          {/* Fitness Track */}
          <button
            onClick={() => setSelectedTrack('fitness')}
            data-testid="track-select-fitness"
            className="group bg-white border-2 border-[#E5E7EB] hover:border-[#4ECDC4] rounded-2xl p-8 text-left transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-[#4ECDC4]/10 relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#4ECDC4] to-[#6B5CE7] opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <div className="w-14 h-14 rounded-[14px] bg-gradient-to-br from-[#4ECDC4]/10 to-[#6B5CE7]/10 flex items-center justify-center mb-5">
              <Dumbbell className="h-7 w-7 text-[#4ECDC4]" />
            </div>
            <h3
              className="font-extrabold text-[#1A1A1A] text-xl uppercase mb-2 tracking-wide"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Boutique Fitness Studio
            </h3>
            <p className="text-[#6B7280] text-sm leading-relaxed mb-6">
              Gyms, yoga studios, pilates, CrossFit boxes, personal training, and martial arts studios.
            </p>
            <span className="inline-flex items-center gap-1.5 text-sm font-bold text-[#4ECDC4] uppercase tracking-wider" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Start Application <ChevronRight className="h-4 w-4" />
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnifiedWizard;
